import { Group, Quaternion } from 'three';
import { TransformControls } from 'three/addons/controls/TransformControls.js';
import type { MagicBallAPI } from '../../api/MagicBallAPI';
import type { MagicBallScene } from './MagicBallScene';
import { messages, environmentPresets } from './MagicBallConfig';

export class DebugMenu {
  private container: HTMLDivElement;
  private orientationActive = false;
  private orientGroup: Group | null = null;
  private gizmo: TransformControls | null = null;
  private readout: HTMLDivElement | null = null;
  private scene: MagicBallScene;
  private readoutInterval: number | null = null;
  private revealBtn: HTMLButtonElement | null = null;

  constructor(api: MagicBallAPI, scene: MagicBallScene) {
    this.scene = scene;

    this.container = document.createElement('div');
    Object.assign(this.container.style, {
      position: 'fixed',
      top: '0',
      right: '0',
      width: '280px',
      height: '100vh',
      background: 'rgba(0,0,0,0.8)',
      color: '#fff',
      padding: '12px',
      borderLeft: '1px solid #333',
      fontFamily: 'system-ui, sans-serif',
      fontSize: '12px',
      overflowY: 'auto',
      zIndex: '1000',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    });

    // --- Hide/Show toggle ---
    const revealBtn = document.createElement('button');
    revealBtn.textContent = '🛠';
    Object.assign(revealBtn.style, {
      position: 'fixed', top: '12px', right: '12px', width: '44px', height: '44px',
      borderRadius: '50%', border: '1px solid #555', background: 'rgba(0,0,0,0.7)',
      color: '#fff', cursor: 'pointer', fontSize: '20px', zIndex: '1001', display: 'none',
    });
    revealBtn.addEventListener('click', () => {
      this.container.style.display = 'flex';
      revealBtn.style.display = 'none';
    });
    document.body.appendChild(revealBtn);
    this.revealBtn = revealBtn;

    const hideBtn = document.createElement('button');
    hideBtn.textContent = 'Hide panel ✕';
    Object.assign(hideBtn.style, {
      padding: '6px 10px', borderRadius: '4px', border: '1px solid #555',
      background: '#222', color: '#aaa', cursor: 'pointer', fontSize: '12px', width: '100%',
    });
    hideBtn.addEventListener('click', () => {
      this.container.style.display = 'none';
      revealBtn.style.display = 'block';
    });
    this.container.appendChild(hideBtn);

    // --- Orientation Mode ---
    this.addTitle('Orientation Setup');

    const orientBtn = document.createElement('button');
    orientBtn.textContent = 'Enable Orientation Mode';
    Object.assign(orientBtn.style, {
      padding: '6px 10px', borderRadius: '4px', border: '2px solid #f80',
      background: '#432', color: '#fff', cursor: 'pointer', fontSize: '12px', width: '100%', fontWeight: '600',
    });
    orientBtn.addEventListener('click', () => {
      this.orientationActive = !this.orientationActive;
      orientBtn.textContent = this.orientationActive ? 'Disable Orientation Mode' : 'Enable Orientation Mode';
      orientBtn.style.background = this.orientationActive ? '#f80' : '#432';
      orientBtn.style.color = this.orientationActive ? '#000' : '#fff';
      this.toggleOrientationMode();
    });
    this.container.appendChild(orientBtn);

    this.readout = document.createElement('div');
    this.readout.style.cssText = 'font-family:monospace; font-size:10px; color:#8f8; background:#111; padding:6px; border-radius:4px; white-space:pre-wrap; display:none;';
    this.container.appendChild(this.readout);

    this.addButton('Copy Setup Code', () => this.copySetup());

    this.addSeparator();

    // --- Controls ---
    this.addTitle('Controls');
    this.addButton('Randomize', () => api.randomize());
    this.addButton('Simulate Shake', () => api.simulateShake());

    const faceSelect = this.addSelect(
      'Message',
      messages.map((m, i) => ({ value: String(i), label: `${i}: ${m}` })),
      String(api.getState().currentFace),
      (v) => api.showFace(parseInt(v, 10)),
    );

    const controller = scene.getController();
    if (controller) {
      const prev = controller.onFaceChange;
      controller.onFaceChange = (i: number) => {
        if (typeof prev === 'function') prev(i);
        faceSelect.value = String(i);
      };
    }

    this.addCheckbox('Auto-rotate camera', false, (v) => api.setAutoRotate(v));

    this.addSeparator();
    this.addTitle('Ball Appearance');
    this.addColorPicker('Ball color', '#0084ff', (c) => api.setBallColor(c));
    this.addColorPicker('Emissive color', '#1ae4ff', (c) => api.setEmissiveColor(c));
    this.addColorPicker('Window tint', '#ffffff', (c) => api.setWindowTint(c));
    this.addSlider('Roughness', 0, 1, 0, 0.01, (v) => api.setAppearance({ roughness: v }));
    this.addSlider('Clearcoat', 0, 1, 1.0, 0.01, (v) => api.setAppearance({ clearcoat: v }));
    this.addSlider('Window opacity', 0, 1, 0.82, 0.01, (v) => api.setAppearance({ windowOpacity: v }));
    this.addSlider('Emissive intensity', 0, 10, 5.8, 0.1, (v) => api.setAppearance({ emissiveIntensity: v }));

    this.addSeparator();
    this.addTitle('Environment');
    this.addSelect(
      'Preset',
      Object.keys(environmentPresets).map((k) => ({ value: k, label: k })),
      'nebula',
      (v) => api.loadEnvironment(v),
    );
    this.addSlider('Env intensity', 0, 2, 0.3, 0.05, (v) => api.setEnvironment({ intensity: v }));
    this.addCheckbox('Show as background', true, (v) => api.setEnvironment({ showAsBackground: v }));
    this.addColorPicker('Background color', '#000000', (c) => api.setBackgroundColor(c));

    this.addSeparator();
    this.addTitle('Localization');
    this.buildLocalizationSection(api);

    this.addSeparator();
    this.addTitle('Text Calibration');
    this.buildCalibrationSection(api);

    for (const child of Array.from(this.container.children)) {
      (child as HTMLElement).style.flexShrink = '0';
    }

    document.body.appendChild(this.container);
  }

  private buildLocalizationSection(api: MagicBallAPI): void {
    const langSelect = this.addSelect('Language', [{ value: 'en', label: 'en: English' }], 'en', (v) => {
      api.setLanguage(v);
    });

    api.getLanguages().then((langs) => {
      langSelect.innerHTML = '';
      for (const l of langs) {
        const o = document.createElement('option');
        o.value = l.code;
        o.textContent = `${l.code}: ${l.nativeName}`;
        langSelect.appendChild(o);
      }
      const customOpt = document.createElement('option');
      customOpt.value = 'custom';
      customOpt.textContent = 'custom: Custom messages';
      langSelect.appendChild(customOpt);
      langSelect.value = api.getCurrentLanguage();
    });

    this.addButton('Edit Messages…', () => this.openMessagesModal(api, langSelect));
  }

  private openMessagesModal(api: MagicBallAPI, langSelect: HTMLSelectElement): void {
    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
      position: 'fixed', inset: '0', background: 'rgba(0,0,0,0.6)',
      zIndex: '2000', display: 'flex', alignItems: 'center', justifyContent: 'center',
    });

    const panel = document.createElement('div');
    Object.assign(panel.style, {
      background: '#1a1a1f', color: '#fff', borderRadius: '10px',
      border: '1px solid #444', padding: '16px', width: '420px',
      maxHeight: '85vh', display: 'flex', flexDirection: 'column', gap: '10px',
      fontFamily: 'system-ui, sans-serif', fontSize: '13px',
      boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
    });

    const title = document.createElement('div');
    title.textContent = 'Edit Messages';
    title.style.cssText = 'font-weight:700; font-size:15px;';
    panel.appendChild(title);

    const list = document.createElement('div');
    Object.assign(list.style, {
      overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px',
      flex: '1', minHeight: '0', paddingRight: '4px',
    });

    const inputs: HTMLInputElement[] = [];
    const current = api.getFaces();
    for (let i = 0; i < 20; i++) {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex; align-items:center; gap:8px; flex-shrink:0;';

      const lbl = document.createElement('span');
      lbl.textContent = String(i);
      lbl.style.cssText = 'width:20px; text-align:right; color:#888; font-size:11px; flex-shrink:0;';

      const input = document.createElement('input');
      input.type = 'text';
      input.value = current[i]?.message ?? '';
      Object.assign(input.style, {
        flex: '1', padding: '6px 8px', background: '#222', color: '#fff',
        border: '1px solid #555', borderRadius: '4px', fontSize: '12px',
      });

      inputs.push(input);
      row.append(lbl, input);
      list.appendChild(row);
    }
    panel.appendChild(list);

    const btnRow = document.createElement('div');
    btnRow.style.cssText = 'display:flex; gap:8px; justify-content:flex-end; flex-shrink:0;';

    const mkBtn = (label: string, bg: string) => {
      const b = document.createElement('button');
      b.textContent = label;
      Object.assign(b.style, {
        padding: '8px 16px', borderRadius: '6px', border: '1px solid #555',
        background: bg, color: '#fff', cursor: 'pointer', fontSize: '13px',
      });
      return b;
    };

    const cancelBtn = mkBtn('Cancel', '#333');
    cancelBtn.addEventListener('click', () => overlay.remove());

    const applyBtn = mkBtn('Apply', '#2a6');
    applyBtn.addEventListener('click', () => {
      const values = inputs.map((inp) => inp.value.trim());
      api.setCustomMessages(values);
      langSelect.value = 'custom';
      overlay.remove();
    });

    btnRow.append(cancelBtn, applyBtn);
    panel.appendChild(btnRow);

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) overlay.remove();
    });

    overlay.appendChild(panel);
    document.body.appendChild(overlay);
  }

  private calibOverrides: { rotation?: number; radius?: number; center?: [number, number] }[] =
    Array.from({ length: 20 }, () => ({}));
  private calibFace = 0;

  private buildCalibrationSection(api: MagicBallAPI): void {
    const faceSelect = this.addSelect(
      'Face',
      messages.map((m, i) => ({ value: String(i), label: `${i}: ${m}` })),
      '0',
      (v) => { this.calibFace = parseInt(v, 10); },
    );
    void faceSelect;

    const regen = () => {
      const msgs = api.getFaces().map((f) => f.message);
      const sourceImage = this.scene.getIcoSourceImage();
      if (!sourceImage) return;
      import('./MessageTextureGenerator').then(({ MessageTextureGenerator }) => {
        import('./faceUVMap').then(({ faceUVMap }) => {
          const overrides = this.calibOverrides.map((o, i) => ({
            rotation: o.rotation !== undefined ? faceUVMap[i].rotation + o.rotation : undefined,
            radius: o.radius !== undefined ? faceUVMap[i].radius * o.radius : undefined,
            center: o.center !== undefined
              ? [faceUVMap[i].center[0] + o.center[0], faceUVMap[i].center[1] + o.center[1]] as [number, number]
              : undefined,
          }));
          const gen = new MessageTextureGenerator(sourceImage);
          const tex = gen.generate(msgs, overrides);
          this.scene.applyMessageTextures(tex);
        });
      });
    };

    this.addSlider('Rotation offset (deg)', -180, 180, 0, 1, (v) => {
      this.calibOverrides[this.calibFace].rotation = (v * Math.PI) / 180;
      regen();
    });
    this.addSlider('Scale', 0.3, 2, 1, 0.05, (v) => {
      this.calibOverrides[this.calibFace].radius = v;
      regen();
    });
    this.addSlider('U offset', -0.05, 0.05, 0, 0.001, (v) => {
      const c = this.calibOverrides[this.calibFace].center ?? [0, 0];
      this.calibOverrides[this.calibFace].center = [v, c[1]];
      regen();
    });
    this.addSlider('V offset', -0.05, 0.05, 0, 0.001, (v) => {
      const c = this.calibOverrides[this.calibFace].center ?? [0, 0];
      this.calibOverrides[this.calibFace].center = [c[0], v];
      regen();
    });

    this.addButton('Copy Calibrated UV Map', () => {
      import('./faceUVMap').then(({ faceUVMap }) => {
        const merged = faceUVMap.map((f, i) => {
          const o = this.calibOverrides[i];
          return {
            center: o.center ? [f.center[0] + o.center[0], f.center[1] + o.center[1]] : f.center,
            rotation: o.rotation !== undefined ? f.rotation + o.rotation : f.rotation,
            radius: o.radius !== undefined ? f.radius * o.radius : f.radius,
          };
        });
        const code = 'export const faceUVMap: FaceUVPlacement[] = [\n' +
          merged.map((m) => `  { center: [${(m.center[0] as number).toFixed(5)}, ${(m.center[1] as number).toFixed(5)}], rotation: ${m.rotation.toFixed(5)}, radius: ${m.radius.toFixed(5)} },`).join('\n') +
          '\n];';
        navigator.clipboard.writeText(code);
      });
    });
  }

  private toggleOrientationMode(): void {
    const cam = this.scene.getCameraSystem();
    const ballShell = this.scene.getBallShellGroup();
    const icoGroup = this.scene.getIcoGroup();
    const threeScene = this.scene.getThreeScene();
    const interaction = this.scene.getInteraction();

    if (!cam || !ballShell || !icoGroup || !threeScene) return;

    if (this.orientationActive) {
      interaction?.setEnabled(false);

      this.orientGroup = new Group();
      this.orientGroup.name = 'orientGroup';
      const ballParent = ballShell.parent!;
      const icoParent = icoGroup.parent!;

      ballShell.removeFromParent();
      icoGroup.removeFromParent();
      this.orientGroup.add(ballShell);
      this.orientGroup.add(icoGroup);
      ballParent.add(this.orientGroup);

      // store parent ref for restore
      (this.orientGroup as unknown as { _ballParent: typeof ballParent; _icoParent: typeof icoParent })._ballParent = ballParent;
      (this.orientGroup as unknown as { _icoParent: typeof icoParent })._icoParent = icoParent;

      this.gizmo = new TransformControls(cam.camera, cam.controls.domElement as HTMLElement);
      this.gizmo.setMode('rotate');
      this.gizmo.setSize(0.8);
      this.gizmo.attach(this.orientGroup);
      this.gizmo.addEventListener('dragging-changed', (event) => {
        cam.controls.enabled = !event.value;
      });
      this.gizmo.addEventListener('change', () => this.updateReadout());
      threeScene.add(this.gizmo.getHelper());

      cam.controls.enablePan = true;

      if (this.readout) this.readout.style.display = 'block';
      this.updateReadout();
      this.readoutInterval = window.setInterval(() => this.updateReadout(), 200);

    } else {
      interaction?.setEnabled(true);

      if (this.gizmo) {
        this.gizmo.detach();
        this.gizmo.getHelper().removeFromParent();
        this.gizmo.dispose();
        this.gizmo = null;
      }

      if (this.orientGroup) {
        const ballShellRef = this.scene.getBallShellGroup();
        const icoGroupRef = this.scene.getIcoGroup();
        const parent = this.orientGroup.parent;

        if (ballShellRef && icoGroupRef && parent) {
          const worldQ = new Quaternion();
          this.orientGroup.getWorldQuaternion(worldQ);

          ballShellRef.removeFromParent();
          icoGroupRef.removeFromParent();
          this.orientGroup.removeFromParent();

          parent.add(ballShellRef);
          parent.add(icoGroupRef);

          ballShellRef.quaternion.premultiply(worldQ);
          icoGroupRef.quaternion.premultiply(worldQ);
        }

        this.orientGroup = null;
      }

      if (cam) cam.controls.enablePan = false;

      if (this.readoutInterval !== null) {
        clearInterval(this.readoutInterval);
        this.readoutInterval = null;
      }
      if (this.readout) this.readout.style.display = 'none';
    }
  }

  private updateReadout(): void {
    if (!this.readout) return;
    const cam = this.scene.getCameraSystem();
    const p = cam?.camera.position;
    const t = cam?.controls.target;

    let ballQ = 'identity';
    if (this.orientGroup) {
      const q = this.orientGroup.quaternion;
      ballQ = `[${q.x.toFixed(6)}, ${q.y.toFixed(6)}, ${q.z.toFixed(6)}, ${q.w.toFixed(6)}]`;
    }

    this.readout.textContent =
      `Camera pos: [${p?.x.toFixed(4)}, ${p?.y.toFixed(4)}, ${p?.z.toFixed(4)}]\n` +
      `Camera target: [${t?.x.toFixed(4)}, ${t?.y.toFixed(4)}, ${t?.z.toFixed(4)}]\n` +
      `Ball orient: ${ballQ}`;
  }

  private copySetup(): void {
    const cam = this.scene.getCameraSystem();
    const p = cam?.camera.position;
    const t = cam?.controls.target;

    let ballQ: number[] | null = null;
    if (this.orientGroup) {
      const q = this.orientGroup.quaternion;
      ballQ = [q.x, q.y, q.z, q.w];
    }

    const code = [
      `// Camera`,
      `position: [${p?.x.toFixed(4)}, ${p?.y.toFixed(4)}, ${p?.z.toFixed(4)}],`,
      `target: [${t?.x.toFixed(4)}, ${t?.y.toFixed(4)}, ${t?.z.toFixed(4)}],`,
      ``,
      `// Ball orientation (quaternion [x, y, z, w])`,
      ballQ ? `ballOrientation: [${ballQ.map(v => v.toFixed(6)).join(', ')}],` : `ballOrientation: null, // not changed`,
    ].join('\n');

    console.log(code);

    const showInReadout = (header: string) => {
      if (!this.readout) return;
      if (this.readoutInterval !== null) {
        clearInterval(this.readoutInterval);
        this.readoutInterval = null;
      }
      this.readout.style.display = 'block';
      (this.readout.style as unknown as { userSelect: string }).userSelect = 'text';
      this.readout.textContent = `${header}\n${code}`;
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(code)
        .then(() => showInReadout('Copied to clipboard:'))
        .catch(() => showInReadout('Clipboard unavailable — copy manually:'));
    } else {
      // iOS Safari over HTTP has no clipboard API — show on screen for manual copy
      showInReadout('Clipboard unavailable — copy manually:');
    }
  }

  // --- UI Helpers ---

  private addTitle(text: string): void {
    const el = document.createElement('div');
    el.textContent = text;
    el.style.cssText = 'font-weight:700; font-size:13px; margin-top:4px; color:#ccc;';
    this.container.appendChild(el);
  }

  private addSeparator(): void {
    const el = document.createElement('hr');
    el.style.cssText = 'border:none; border-top:1px solid #333; margin:4px 0;';
    this.container.appendChild(el);
  }

  private addButton(label: string, onClick: () => void): void {
    const btn = document.createElement('button');
    btn.textContent = label;
    Object.assign(btn.style, {
      padding: '5px 10px', borderRadius: '4px', border: '1px solid #555',
      background: '#333', color: '#fff', cursor: 'pointer', fontSize: '12px', width: '100%',
    });
    btn.addEventListener('click', onClick);
    this.container.appendChild(btn);
  }

  private addSelect(
    label: string,
    options: { value: string; label: string }[],
    defaultValue: string,
    onChange: (value: string) => void,
  ): HTMLSelectElement {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex; flex-direction:column; gap:2px;';
    const lbl = document.createElement('label');
    lbl.textContent = label;
    lbl.style.cssText = 'font-size:11px; color:#888;';
    const sel = document.createElement('select');
    Object.assign(sel.style, {
      padding: '3px 6px', borderRadius: '4px', border: '1px solid #555',
      background: '#222', color: '#fff', fontSize: '11px',
    });
    for (const opt of options) {
      const o = document.createElement('option');
      o.value = opt.value;
      o.textContent = opt.label;
      sel.appendChild(o);
    }
    sel.value = defaultValue;
    sel.addEventListener('change', () => onChange(sel.value));
    row.append(lbl, sel);
    this.container.appendChild(row);
    return sel;
  }

  private addSlider(
    label: string, min: number, max: number, defaultVal: number, step: number,
    onChange: (value: number) => void,
  ): void {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex; flex-direction:column; gap:2px;';
    const header = document.createElement('div');
    header.style.cssText = 'display:flex; justify-content:space-between; font-size:11px; color:#888;';
    const lbl = document.createElement('span');
    lbl.textContent = label;
    const val = document.createElement('span');
    val.textContent = String(defaultVal);
    header.append(lbl, val);
    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = String(min);
    slider.max = String(max);
    slider.step = String(step);
    slider.value = String(defaultVal);
    slider.style.cssText = 'width:100%; accent-color:#88f;';
    slider.addEventListener('input', () => {
      const v = parseFloat(slider.value);
      val.textContent = v.toFixed(2);
      onChange(v);
    });
    row.append(header, slider);
    this.container.appendChild(row);
  }

  private addColorPicker(label: string, defaultColor: string, onChange: (color: string) => void): void {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex; align-items:center; gap:8px;';
    const lbl = document.createElement('span');
    lbl.textContent = label;
    lbl.style.cssText = 'font-size:11px; color:#888; flex:1;';
    const input = document.createElement('input');
    input.type = 'color';
    input.value = defaultColor;
    input.style.cssText = 'width:40px; height:24px; border:1px solid #555; border-radius:4px; background:none; cursor:pointer;';
    input.addEventListener('input', () => onChange(input.value));
    row.append(lbl, input);
    this.container.appendChild(row);
  }

  private addCheckbox(label: string, defaultChecked: boolean, onChange: (checked: boolean) => void): void {
    const row = document.createElement('label');
    row.style.cssText = 'display:flex; align-items:center; gap:6px; cursor:pointer; font-size:11px; color:#ccc;';
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.checked = defaultChecked;
    cb.addEventListener('change', () => onChange(cb.checked));
    row.append(cb, document.createTextNode(label));
    this.container.appendChild(row);
  }

  dispose(): void {
    if (this.orientationActive) {
      this.orientationActive = false;
      this.toggleOrientationMode();
    }
    this.revealBtn?.remove();
    this.container.remove();
  }
}
